import React, { useState, useEffect } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { STRIPE_ELEMENTS_CONFIG, validateStripeConfig } from '../config/stripe';

const StripePaymentForm = ({ 
  amount, 
  description, 
  onSuccess, 
  onError, 
  onCancel,
  isSetupIntent = false, // true for setup intent (no charge), false for payment intent
  metadata = {},
  buttonText = 'Pay Now',
  loadingText = 'Processing...'
 }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [configError, setConfigError] = useState('');
  const [success, setSuccess] = useState(false);

  // Check Stripe configuration on mount
  useEffect(() => {
    if (!validateStripeConfig()) {
      setConfigError('Stripe is not properly configured. Please check the console for setup instructions.');
    }
  }, []);

  // Check if Stripe Elements loaded properly
  useEffect(() => {
    if (stripe && elements) {
      console.log('Stripe Elements loaded successfully');
    } else if (stripe && !elements) {
      console.warn('Stripe loaded but Elements not ready');
    } else if (!stripe) {
      console.warn('Stripe not loaded yet');
    }
  }, [stripe, elements]);

  // Debug logging
  useEffect(() => {
    console.log('StripePaymentForm mounted:', {
      stripe: !!stripe,
      elements: !!elements,
      isSetupIntent,
      amount,
      description
    });
  }, [stripe, elements, isSetupIntent, amount, description]);

  // Card element options
  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#1f2937',
        '::placeholder': {
          color: '#9ca3af',
        },
        iconColor: '#3b82f6',
      },
      invalid: {
        color: '#ef4444',
        iconColor: '#ef4444',
      },
    },
    hidePostalCode: false,
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    console.log('=== FORM SUBMISSION START ===');
    console.log('Config error:', configError);
    console.log('Stripe ready:', !!stripe);
    console.log('Elements ready:', !!elements);
    console.log('Client secret:', clientSecret);
    console.log('Is setup intent:', isSetupIntent);
    
    // Prevent submission if Stripe is not configured
    if (configError) {
      setError('Please configure Stripe first. See the instructions above.');
      return;
    }
    
    console.log('Form submitted, checking Stripe availability...');
    
    if (!stripe || !elements) {
      const errorMsg = `Stripe not ready: stripe=${!!stripe}, elements=${!!elements}`;
      console.error(errorMsg);
      setError(errorMsg);
      return;
    }

    // Validate client secret format
    if (!clientSecret || !clientSecret.includes('_secret_')) {
      const errorMsg = 'Invalid client secret format. Please refresh the page and try again.';
      console.error(errorMsg, 'Client secret:', clientSecret);
      setError(errorMsg);
      return;
    }

    console.log('All checks passed, proceeding with payment...');
    setLoading(true);
    setError('');

    try {
      console.log('Getting card element...');
      // Get card element
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      console.log('Creating payment method...');
      // Create payment method
      const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: metadata.cardholderName || '',
          email: metadata.email || '',
          address: {
            line1: metadata.billingAddress || '',
            city: metadata.city || '',
            state: metadata.state || '',
            postal_code: metadata.zipCode || '',
            country: 'US',
          },
        },
      });

      if (paymentMethodError) {
        console.error('Payment method creation error:', paymentMethodError);
        throw new Error(paymentMethodError.message);
      }

      console.log('Payment method created successfully:', paymentMethod.id);

      // If this is a setup intent (no charge), confirm the setup
      if (isSetupIntent) {
        console.log('Processing setup intent (mock mode)...');
        console.log('Payment method ID:', paymentMethod.id);
        
        try {
          // Since we're using mock client secrets, simulate the setup success
          // In production, you would call stripe.confirmSetup() with real client secrets
          console.log('Simulating setup intent success for testing...');
          
          // Simulate a delay to show the loading state
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          console.log('Setup successful (simulated)');
          // Setup successful - simulate the response
          setSuccess(true);
          setLoading(false);
          
          // Call onSuccess after a brief delay to show the success message
          setTimeout(() => {
            onSuccess({
              type: 'setup',
              paymentMethodId: paymentMethod.id,
              setupIntent: { 
                status: 'succeeded',
                id: clientSecret.split('_secret_')[0], // Extract the intent ID
                client_secret: clientSecret
              }
            });
          }, 1500);
        } catch (setupError) {
          console.error('Setup confirmation failed:', setupError);
          throw setupError;
        }
      } else {
        console.log('Processing payment intent (mock mode)...');
        console.log('Payment method ID:', paymentMethod.id);
        
        try {
          // Since we're using mock client secrets, simulate the payment success
          // In production, you would call stripe.confirmPayment() with real client secrets
          console.log('Simulating payment intent success for testing...');
          
          // Simulate a delay to show the loading state
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          console.log('Payment successful (simulated)');
          // Payment successful - simulate the response
          setSuccess(true);
          setLoading(false);
          
          // Call onSuccess after a brief delay to show the success message
          setTimeout(() => {
            onSuccess({
              type: 'payment',
              paymentMethodId: paymentMethod.id,
              paymentIntent: { 
                status: 'succeeded',
                id: clientSecret.split('_secret_')[0], // Extract the intent ID
                client_secret: clientSecret,
                amount: amount
              }
            });
          }, 1500);
        } catch (paymentError) {
          console.error('Payment confirmation failed:', paymentError);
          throw paymentError;
        }
      }
    } catch (error) {
      console.error('Payment error:', error);
      setError(error.message || 'Payment failed. Please try again.');
      onError?.(error);
    } finally {
      setLoading(false);
    }
  };

  // Set client secret when component mounts
  useEffect(() => {
    if (clientSecret) {
      return;
    }

    console.log('Generating mock client secret for testing...');
    // For testing purposes, generate a proper client secret format
    // In production, you would get this from your backend API
    if (isSetupIntent) {
      // Setup intent format: seti_xxx_secret_xxx (24 chars + secret + 24 chars)
      const mockSetupIntentId = 'seti_' + Math.random().toString(36).substr(2, 19);
      const mockSecret = Math.random().toString(36).substr(2, 24);
      const mockClientSecret = `${mockSetupIntentId}_secret_${mockSecret}`;
      console.log('Generated mock setup intent client secret:', mockClientSecret);
      setClientSecret(mockClientSecret);
    } else {
      // Payment intent format: pi_xxx_secret_xxx (24 chars + secret + 24 chars)
      const mockPaymentIntentId = 'pi_' + Math.random().toString(36).substr(2, 21);
      const mockSecret = Math.random().toString(36).substr(2, 24);
      const mockClientSecret = `${mockPaymentIntentId}_secret_${mockSecret}`;
      console.log('Generated mock payment intent client secret:', mockClientSecret);
      setClientSecret(mockClientSecret);
    }
  }, [clientSecret, isSetupIntent]);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Development Notice */}
      {process.env.NODE_ENV === 'development' && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm text-blue-700">
              <strong>Development Mode:</strong> Running in mock mode with simulated Stripe responses. Payment methods are created with Stripe but setup/payment confirmations are simulated for testing. In production, you'll need a backend API to generate real Stripe client secrets.
            </span>
          </div>
        </div>
      )}

      {configError && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-yellow-800 mb-2">Stripe Configuration Required</h3>
              <p className="text-sm text-yellow-700 mb-3">{configError}</p>
              <div className="bg-yellow-100 p-3 rounded border border-yellow-200">
                <p className="text-xs text-yellow-800 font-medium mb-2">Quick Fix:</p>
                <ol className="text-xs text-yellow-800 space-y-1 list-decimal list-inside">
                  <li>Go to <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline">Stripe Dashboard</a></li>
                  <li>Copy your test publishable key (starts with pk_test_)</li>
                  <li>Create .env.local file in project root</li>
                  <li>Add: REACT_APP_STRIPE_PUBLISHABLE_KEY=your_key_here</li>
                  <li>Restart your development server</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      )}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-sm text-green-700">
              <strong>Success!</strong> Payment method saved successfully. Processing registration...
            </span>
          </div>
        </div>
      )}

      {/* Payment Amount Display */}
      {!isSetupIntent && amount > 0 && (
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              ${(amount / 100).toFixed(2)}
            </div>
            <div className="text-sm text-gray-600">{description}</div>
          </div>
        </div>
      )}

      {/* Setup Intent Notice */}
      {isSetupIntent && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm text-blue-700">
              No charges will be made during your trial period. Your payment method will be securely stored for future billing.
            </span>
          </div>
        </div>
      )}

      {/* Card Element */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Card Information
        </label>
        {!stripe || !elements ? (
          <div className="border border-gray-300 rounded-lg p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
            <p className="text-sm text-gray-600">Loading payment form...</p>
          </div>
        ) : (
          <div className="border border-gray-300 rounded-lg p-3 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent">
            <CardElement options={cardElementOptions} />
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex space-x-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="flex-1 bg-gray-300 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={!stripe || loading || !!configError || !elements}
          className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {!stripe || !elements ? 'Loading...' : loading ? loadingText : buttonText}
        </button>
      </div>

      {/* Security Notice */}
      <div className="text-center">
        <p className="text-xs text-gray-500">
          🔒 Your payment information is secure and encrypted by Stripe
        </p>
      </div>

      {/* Debug Info (remove in production) */}
      <div className="text-xs text-gray-400 bg-gray-50 p-2 rounded">
        <p>Debug: Stripe={!!stripe}, Elements={!!elements}</p>
        <p>Client Secret: {clientSecret ? 'Set' : 'Not Set'}</p>
        <p>Client Secret Format: {clientSecret ? (clientSecret.includes('_secret_') ? '✅ Valid' : '❌ Invalid') : 'N/A'}</p>
        <p>Intent Type: {isSetupIntent ? 'Setup Intent' : 'Payment Intent'}</p>
        <p className="text-green-600">✅ Mock Mode Active</p>
        <p className="text-xs">Payment methods created with Stripe, confirmations simulated</p>
        <p className="text-xs">In production, implement backend API for real client secrets</p>
      </div>
    </form>
  );
};

export default StripePaymentForm;
